// @givmo/board — EVERY word CBG-RC12-REC's screens add, in one place: a closed session recorded as
// practice records it (D-CBG-25) — its general purpose, who stepped out of it for a conflict of
// interest, and the board's choice to keep a decision made in it out of the meeting's minutes.
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the frames for building on 2026-10-12 22:52 PDT ("looks good"),
// with every word kept a draft, and then the words themselves in copy round CBG-RC12-COPY1 on
// 2026-10-13 09:27 PDT ("2) approved"), every line as drawn, the two renamed general purposes
// included. Each line names the frame that draws it (`r1-session`, `r2-decision`, `c4-decision`; the
// design branch `design/board-minutes-approval-closed-sessions` @ `8d5c8ee8`), or "not drawn" for a
// state no frame shows.
//
// FOUR drawn lines shipped SHORTENED until CBG-RC12-READ (the orchestrator's ruling C4, 2026-10-12
// 23:12 PDT): each frame line ends "…, and any director can open it/them", which is RC12-READ's
// behavior. RC12-READ restored the clause in the frames' own words once every current director could
// open a closed session's minutes, and the copy round's second round approved them (2026-10-13
// 14:48 PDT, "4)ok"); they are marked "restored by CBG-RC12-READ" below. The words RC12-READ's own
// screens add are `../director/closed-minutes-words.ts`.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the board recorded, and where it goes, never a
// conclusion about a matter, a conflict or a decision. Board never classifies a matter: the general
// purpose is the officer's choice of words. No pronoun for a named person.

/** The closed-session words, approved in copy round CBG-RC12-COPY1. */
export const closedSessionWords = {
  // ── The sessions card ("How the meeting ran") ─────────────────────────────────────────────────

  // Drawn, r2-decision; restored by CBG-RC12-READ to the frame's whole line.
  sessionsLead:
    'The sessions the meeting was made of, in order. An executive session keeps its own record of who was in the room; it’s sent to those people, and any director can open it.',

  // ── The session dialog ──────────────────────────────────────────────────────────────────────────

  // Drawn, r1-session (the fixed kind, on "Edit this session").
  kindFixedNote:
    'This can’t be changed. It decides who everything recorded inside this session is sent to. Remove the session and add the right kind instead.',
  // Drawn on the swap (the kind chosen on "Add a session"); restored by
  // CBG-RC12-READ to the frame's whole line.
  kindNewNote:
    'An executive session’s record is sent to the people you record as having been in it, and any director can open it. You can’t change this afterwards.',
  // Drawn, r1-session.
  purposeLabel: 'General purpose',
  purposePlaceholder: 'Choose one',
  purposeNote: 'Shown in the meeting’s minutes, which everyone on your board reads.',
  // Drawn, r1-session (the list; "A conflict-of-interest matter" chosen).
  // Board never classifies a matter: these are words the officer picks, and "Something else" is the
  // board's own. ⚠ TWO DIFFER FROM THE FRAME: its "Legal advice" and "Meeting with the auditor" trip
  // this app's copy rail (§3.4's `legal-advice-counsel-reviewed` and `audit` terms), and owned copy
  // that trips the rail is renamed, never exempted (`terminology-rail.test.ts`). The copy round
  // shows both changes. "Meeting with the independent accountants" speaks the suite list's
  // "independent" only through D-CBG-29's third widening, bound on this file alone
  // (`@givmo/suite-terminology` `closed-session-purposes`).
  purposeLabels: [
    'Personnel',
    'Compensation',
    'Meeting with counsel',
    'Meeting with the independent accountants',
    'A conflict-of-interest matter',
  ] as const,
  purposeSomethingElse: 'Something else',
  // Not drawn: the field "Something else" opens (the orchestrator's call C8).
  purposeOwnWords: 'In your board’s own words',
  // Drawn, r1-session (under the title and the reason).
  keptNote: 'The title and the reason stay in this session’s own minutes.',
  // Drawn, r1-session.
  steppedOutHeading: 'Stepped out for a conflict of interest',
  steppedOutRemove: 'Remove',
  steppedOutAdd: 'Add someone who stepped out',
  steppedOutNote:
    'Names are shown in the meeting’s minutes; each note stays in this session’s own minutes.',
  // Not drawn: the small form "Add someone who stepped out" opens.
  steppedOutSeat: 'Who stepped out',
  steppedOutSeatPlaceholder: 'Choose a seat',
  steppedOutNoteField: 'Note (optional)',
  steppedOutAddButton: 'Add',
  steppedOutCancel: 'Cancel',
  // Not drawn: a Remove link's accessible name.
  steppedOutRemoveLabel: (name: string) => `Remove ${name} from who stepped out`,

  // ── The motions card ("What the board moved") ─────────────────────────────────────────────────

  // Drawn, r2-decision (the switch, off).
  keepConfidential: 'Keep this decision confidential',
  keptConfidentialOff:
    'Not kept confidential, it’s reported in the meeting’s minutes: its wording, the vote, the outcome and who was recused.',
  // Not drawn: the switch on.
  keptConfidentialOn: 'Kept confidential, it stays in this session’s own minutes.',
  // Not drawn: the switch's save failed with no reason served.
  keepConfidentialFailed: 'We couldn’t save that. Please try again.',
  // Drawn on the swap (the motion dialog's session note); restored by
  // CBG-RC12-READ to the frame's whole line.
  motionSessionNote:
    'A motion made in an executive session is sent to the people you recorded as having been in it, and any director can open it.',

  // ── The minutes page ──────────────────────────────────────────────────────────────────────────

  // Drawn, m1-consent and c4-decision ("Which minutes"); restored by
  // CBG-RC12-READ to the frame's whole line.
  whichMinutesLead:
    'This meeting had a closed session. Its own minutes are sent to the people who were in it, and any director can open them.',
} as const;

/** The general purpose as the dialog shows it: one of the offered labels, or the board's own words
 *  under "Something else". */
export interface PurposeChoice {
  choice: string | null;
  ownWords: string;
}

/** Split a recorded general purpose into the dialog's choice and its own-words field. */
export function purposeChoiceOf(recorded: string | null | undefined): PurposeChoice {
  const value = (recorded ?? '').trim();
  if (value.length === 0) return { choice: null, ownWords: '' };
  if ((closedSessionWords.purposeLabels as readonly string[]).includes(value)) {
    return { choice: value, ownWords: '' };
  }
  return { choice: closedSessionWords.purposeSomethingElse, ownWords: value };
}

/** The general purpose the dialog records: the chosen label, the board's own words, or none. */
export function purposeToRecord(purpose: PurposeChoice): string | null {
  if (purpose.choice === null) return null;
  if (purpose.choice === closedSessionWords.purposeSomethingElse) {
    const own = purpose.ownWords.trim();
    return own.length > 0 ? own : null;
  }
  return purpose.choice;
}
